import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Collapse,
    DatePicker,
    Drawer,
    Empty,
    Form,
    Image,
    Input,
    InputNumber,
    List,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FilePdfOutlined,
    FileImageOutlined,
    FileSearchOutlined,
    InboxOutlined,
    ReloadOutlined,
    SearchOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { flightLogRecognitionApi } from './api';
import FlightLogAircraftTabs from './FlightLogAircraftTabs';
import {
    buildChainRowMetaMap,
    createFlightLogBodyRow,
    FlightLogChainStatusBadges,
} from './FlightLogChainIndicators';
import {
    isFieldUnconfirmed,
    ReextractSuggestionsBanner,
    UnconfirmedFieldBadge,
} from './RecognitionAdjustmentHelpers';
import ToolTraceViewer from './ToolTraceViewer';
import { useFlightLogInfiniteScroll } from './useFlightLogInfiniteScroll';


const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

const MAX_FILES_PER_BATCH = 20;
const UPLOAD_RESULT_STORAGE_KEY = 'flightLogRecognition.lastUploadResult';

// 表格垂直滚动：viewport 高度减去固定占用，剩下的留给 Table body 滚动条。
// 这个数是个估算（标题栏 + tab 头 + Card padding + 紧凑上传条 + 筛选区 + 页签动作栏 + 表头 + 底部状态），
// 实测调整。同 FlightMonitor/FlightListView 的 FIXED_OFFSET 思路一致。
const TABLE_SCROLL_FIXED_OFFSET = 420;
const MAX_SIZE_MB = 30;
const ALLOWED_EXT = ['.pdf', '.gif', '.jpg', '.jpeg', '.png', '.webp'];

const TEXT = {
    pending: '\u5f85\u8bc6\u522b',
    recognizing: '\u8bc6\u522b\u4e2d',
    parsed: '\u5df2\u8bc6\u522b',
    reviewRequired: '\u5f85\u590d\u6838',
    failed: '\u8bc6\u522b\u5931\u8d25',
    confirmed: '\u5df2\u786e\u8ba4',
    rejectedStatus: '\u5df2\u62d2\u7edd',
    status: '\u72b6\u6001',
    sourceFile: '\u6587\u4ef6',
    confidence: '\u7f6e\u4fe1\u5ea6',
    actions: '\u64cd\u4f5c',
    view: '\u67e5\u770b',
    flightNo: '\u822a\u73ed\u53f7',
    aircraftReg: '\u673a\u53f7',
    // 机号筛选：可多选 + 支持只输片段（如 WFF），语义与燃油明细列表的机号筛选一致。
    aircraftRegFilter: '\u673a\u53f7\uff08\u53ef\u591a\u9009\uff0c\u652f\u6301\u6a21\u7cca\uff09',
    airport: '\u673a\u573a',
    fileName: '\u6587\u4ef6\u540d',
    reset: '\u91cd\u7f6e',
    search: '\u67e5\u8be2',
    uploadText: '\u70b9\u51fb\u6216\u62d6\u62fdPDF/\u56fe\u7247\u5230\u6b64\u5904\u4e0a\u4f20\u8bc6\u522b',
    uploadHint: `\u652f\u6301PDF/GIF/JPG/PNG/WEBP\uff0c\u5355\u6279\u6700\u591a ${MAX_FILES_PER_BATCH} \u4e2a\uff0c\u5355\u4e2a\u6587\u4ef6\u6700\u5927 ${MAX_SIZE_MB} MB`,
    uploading: '\u4e0a\u4f20\u4e2d...',
    uploadResult: '\u4e0a\u4f20\u7ed3\u679c',
    accepted: '\u5df2\u63a5\u6536',
    rejected: '\u5df2\u62d2\u7edd',
    duplicate: '\u5df2\u5b58\u5728',
    originalStatus: '\u539f\u72b6\u6001',
    viewOriginal: '\u67e5\u770b\u539f\u8bb0\u5f55',
    duplicateOf: '\u91cd\u590d\u4e8e',
    uploadedAt: '\u4e0a\u4f20\u4e8e',
    showLastUploadResult: '\u4e0a\u6b21\u4e0a\u4f20\u7ed3\u679c',
    preCheckTitle: '\u4e0a\u4f20\u9884\u89c8\u4e0e\u67e5\u91cd',
    preCheckLoading: '\u6b63\u5728\u8ba1\u7b97\u6587\u4ef6\u54c8\u5e0c\u5e76\u67e5\u91cd...',
    preCheckSummary: (total, fresh, dup) => `\u5171 ${total} \u4e2a\uff1a${fresh} \u4e2a\u65b0\u6587\u4ef6\uff0c${dup} \u4e2a\u5df2\u5b58\u5728`,
    preCheckFreshSection: '\u65b0\u6587\u4ef6\uff08\u5c06\u4e0a\u4f20\uff09',
    preCheckDupSection: '\u5df2\u5b58\u5728\uff08\u5c06\u8df3\u8fc7\uff09',
    preCheckUploadFresh: (n) => `\u4ec5\u4e0a\u4f20\u65b0\u6587\u4ef6 (${n})`,
    preCheckUploadAll: (n) => `\u5168\u90e8\u63d0\u4ea4 (${n})\uff0c\u91cd\u590d\u7531\u540e\u7aef\u53bb\u91cd`,
    preCheckCancel: '\u53d6\u6d88',
    preCheckFailed: '\u9884\u89c8\u67e5\u91cd\u5931\u8d25\uff0c\u5c06\u8d70\u4f20\u7edf\u4e0a\u4f20\u8def\u5f84',
    emptyRecords: '\u6682\u65e0flight log\u8bc6\u522b\u8bb0\u5f55',
    totalPrefix: '\u5171',
    totalSuffix: '\u6761',
    drawerTitle: 'flight log\u8bc6\u522b\u8be6\u60c5',
    detailLoadFailed: '\u8be6\u60c5\u52a0\u8f7d\u5931\u8d25',
    listLoadFailed: '\u5217\u8868\u52a0\u8f7d\u5931\u8d25',
    uploadFailed: '\u4e0a\u4f20\u5931\u8d25',
    uploadSuccess: '\u4e0a\u4f20\u6210\u529f\uff0c\u5df2\u8fdb\u5165\u8bc6\u522b\u961f\u5217',
    invalidFileType: '\u4ec5\u652f\u6301PDF/GIF/JPG/PNG/WEBP\u6587\u4ef6',
    invalidFileSize: `\u6587\u4ef6\u4e0d\u80fd\u8d85\u8fc7 ${MAX_SIZE_MB} MB`,
    batchTruncated: `\u5355\u6279\u6700\u591a\u4e0a\u4f20 ${MAX_FILES_PER_BATCH} \u4e2a\u6587\u4ef6`,
    reExtract: '\u91cd\u65b0\u8bc6\u522b',
    reExtractQueued: '\u5df2\u52a0\u5165\u91cd\u65b0\u8bc6\u522b\u961f\u5217',
    reExtractFailed: '\u91cd\u65b0\u8bc6\u522b\u5931\u8d25',
    deleteAction: '\u5220\u9664',
    deleteConfirmTitle: '\u786e\u5b9a\u5220\u9664\u8fd9\u6761\u8bc6\u522b\u8bb0\u5f55\uff1f',
    deleteConfirmDesc: '\u5220\u9664\u540e\u4e0d\u4f1a\u51fa\u73b0\u5728\u5217\u8868\u91cc\uff0c\u5982\u9700\u6062\u590d\u8bf7\u8054\u7cfb\u7ba1\u7406\u5458\u3002',
    deleteSuccess: '\u8bb0\u5f55\u5df2\u5220\u9664',
    deleteFailed: '\u5220\u9664\u5931\u8d25',
    deleteOk: '\u5220\u9664',
    deleteCancel: '\u53d6\u6d88',
    batchDeleteAction: '\u6279\u91cf\u5220\u9664',
    exportFiltered: '\u5bfc\u51fa\u5f53\u524d\u7b5b\u9009',
    exportBatch: '\u5bfc\u51fa\u9009\u4e2d',
    exporting: '\u5bfc\u51fa\u4e2d...',
    exportSuccess: '\u5bfc\u51fa\u6210\u529f',
    exportFailed: '\u5bfc\u51fa\u5931\u8d25',
    exportEmpty: '\u5f53\u524d\u7b5b\u9009\u4e0b\u6ca1\u6709\u8bb0\u5f55\uff0c\u65e0\u6cd5\u5bfc\u51fa',
    exportSourceFiles: '\u6253\u5305\u6e90\u6587\u4ef6',
    exportSourceBatch: '\u6253\u5305\u6e90\u6587\u4ef6',
    exportSourceSuccess: '\u6253\u5305\u5b8c\u6210\uff0c\u5f00\u59cb\u4e0b\u8f7d',
    exportSourceFailed: '\u6253\u5305\u6e90\u6587\u4ef6\u5931\u8d25',
    exportSourcePreparing: '\u6b63\u5728\u6253\u5305\u6e90\u6587\u4ef6...',
    batchDeleteSelected: (count) => `\u5df2\u9009 ${count} \u6761`,
    batchDeleteConfirmTitle: (count) => `\u786e\u5b9a\u5220\u9664\u9009\u4e2d\u7684 ${count} \u6761\u8bb0\u5f55\uff1f`,
    batchDeleteConfirmDesc: '\u5220\u9664\u540e\u4e0d\u4f1a\u51fa\u73b0\u5728\u5217\u8868\u91cc\uff0c\u5982\u9700\u6062\u590d\u8bf7\u8054\u7cfb\u7ba1\u7406\u5458\u3002',
    batchDeleteSuccess: (count) => `\u5df2\u5220\u9664 ${count} \u6761\u8bb0\u5f55`,
    batchDeletePartial: (deleted, failed) => `\u5df2\u5220\u9664 ${deleted} \u6761\uff0c${failed} \u6761\u672a\u5904\u7406\uff08\u53ef\u80fd\u5df2\u88ab\u522b\u4eba\u5220\u9664\uff09`,
    batchDeleteFailed: '\u6279\u91cf\u5220\u9664\u5931\u8d25',
    clearSelection: '\u53d6\u6d88\u9009\u4e2d',
    close: '\u5173\u95ed',
    rawResult: '\u539f\u59cb\u8bc6\u522b\u7ed3\u679c',
    sourcePreview: '\u539f\u59cb\u6587\u4ef6',
    previewLoadFailed: '\u539f\u59cb\u6587\u4ef6\u9884\u89c8\u52a0\u8f7d\u5931\u8d25',
    reviewFields: '\u8bc6\u522b\u5b57\u6bb5\u590d\u6838',
    saveReview: '\u4fdd\u5b58\u4fee\u6539',
    confirmReview: '\u786e\u8ba4\u590d\u6838',
    reviewSaveSuccess: '\u590d\u6838\u4fee\u6539\u5df2\u4fdd\u5b58',
    reviewConfirmSuccess: '\u590d\u6838\u5df2\u786e\u8ba4',
    reviewFailed: '\u590d\u6838\u4fdd\u5b58\u5931\u8d25',
    reviewNotes: '\u590d\u6838\u5907\u6ce8',
    reviewReason: '\u5f85\u590d\u6838\u539f\u56e0',
    noReviewReason: '\u672a\u8fd4\u56de\u5f85\u590d\u6838\u539f\u56e0',
    majorMaintenance: '\u91cd\u5927\u7ef4\u4fee',
    // T14 \u8bc6\u522b\u9875\u8868\u683c "\u6765\u6e90" \u5217\u4e0e\u8be6\u60c5\u62bd\u5c49\u6eaf\u6e90\u4fe1\u606f
    sourceColumn: '\u6765\u6e90',
    sourceManualUpload: '\u4eba\u5de5\u4e0a\u4f20',
    sourceExternalCrawl: '\u81ea\u52a8\u6293\u53d6',
    sourceCrawlInfoTitle: '\u6765\u6e90\uff1a\u5916\u90e8\u6293\u53d6',
    sourceCrawlLinkText: (id, site, dateKey) => `\u6293\u53d6\u8bb0\u5f55 #${id}\uff08${site} / ${dateKey}\uff09`,
    sourceCrawlDeleted: '\u539f\u6293\u53d6\u8bb0\u5f55\u5df2\u5220\u9664',
    // \u5bf9\u9f50/\u8865\u5168\uff08S7\uff09\u4e0e\u201c\u63a8\u201d\u6807\u8bb0\uff08S1-S5\uff09
    reconcile: '\u5bf9\u9f50/\u8865\u5168',
    reconciling: '\u5bf9\u9f50\u4e2d...',
    reconcileSuccess: (n) => `\u5bf9\u9f50\u5b8c\u6210\uff0c\u5171\u8865\u5168/\u66f4\u65b0 ${n} \u5904`,
    reconcileNoChange: '\u5bf9\u9f50\u5b8c\u6210\uff0c\u65e0\u9700\u8865\u5168\uff08\u5df2\u662f\u6700\u65b0\uff09',
    reconcileFailed: '\u5bf9\u9f50\u5931\u8d25',
    derivedTag: '\u63a8',
    derivedTagTip: '\u7cfb\u7edf\u63a8\u7b97/\u8865\u5168\u503c\uff08\u975e\u8868\u5355\u5b9e\u8bfb\uff09',
    fleetEmptyTip: '\u673a\u961f\u4e3b\u6570\u636e\u4e3a\u7a7a\uff0c\u6682\u53ea\u8bfb\u5c55\u793a\u73b0\u6709\u503c',
    // \u91cd\u590d\u5355\u636e\u53bb\u91cd\uff08dedup\uff09\uff1a\u88ab\u53d6\u4ee3\u8bb0\u5f55\u6807\u8bc6 + \u8df3\u4e3b\u8bb0\u5f55 + \u4eba\u5de5\u7ffb\u6848
    dedupTag: '\u91cd\u590d\uff08\u5df2\u53bb\u91cd\uff09',  // 重复（已去重）
    dedupTagShort: '\u91cd\u590d',  // 重复（列表内紧凑标签）
    dedupTagTip: '\u4e0e\u4e3b\u8bb0\u5f55\u4e3a\u540c\u4e00\u5f20\u5355\u636e\uff08\u540c \u673a\u53f7/\u65e5\u671f/TL No.\uff09\uff0c\u5df2\u88ab\u53bb\u91cd\u53d6\u4ee3\uff0c\u4e0d\u8fdb\u7ed3\u7b97\u4e0e\u822a\u8ff9\u94fe\u3002',
    dedupDrawerAlert: '\u672c\u8bb0\u5f55\u4e3a\u91cd\u590d\u5355\u636e\uff0c\u5df2\u88ab\u53bb\u91cd\u53d6\u4ee3\uff08\u4e0d\u8fdb\u7ed3\u7b97/\u822a\u8ff9\u94fe\uff09\u3002',
    dedupGotoMain: '\u67e5\u770b\u4e3b\u8bb0\u5f55',
    dedupRestore: '\u8bbe\u4e3a\u4e3b\u8bb0\u5f55',
    dedupRestoreConfirmTitle: '\u628a\u672c\u6761\u8bbe\u4e3a\u53bb\u91cd\u4e3b\u8bb0\u5f55\uff1f',
    dedupRestoreConfirmDesc: '\u5c06\u64a4\u9500\u672c\u6761\u7684\u300c\u5df2\u53d6\u4ee3\u300d\u3001\u6062\u590d\u5176\u53c2\u4e0e\u5bf9\u9f50\uff0c\u5e76\u628a\u540c\u7ec4\u5176\u4f59\u8bb0\u5f55\u6539\u4e3a\u5df2\u62d2\u7edd\u3002',
    dedupRestoreSuccess: '\u5df2\u8bbe\u4e3a\u4e3b\u8bb0\u5f55',
    dedupRestoreFailed: '\u7ffb\u6848\u5931\u8d25',
    // 作废页（page_voided）：整页划线/VOID/作废 → 已拒绝；展示靠 record.page_voided 布尔字段（非 review_reasons），人工可「取消作废」翻案。
    voidedTag: '\u4f5c\u5e9f\u9875',
    voidedTagShort: '\u4f5c\u5e9f',
    voidedTagTip: '\u8be5\u9875\u88ab\u8bc6\u522b\u4e3a\u6574\u9875\u4f5c\u5e9f\uff08\u6574\u9875\u5212\u7ebf / VOID / CANCELLED / \u4f5c\u5e9f\u7ae0\uff09\uff0c\u5df2\u5f52\u4e3a\u300c\u5df2\u62d2\u7edd\u300d\uff0c\u4e0d\u8fdb\u7ed3\u7b97\u4e0e\u822a\u8ff9\u94fe\u3002',
    voidedDrawerAlert: '\u672c\u8bb0\u5f55\u88ab\u8bc6\u522b\u4e3a\u4f5c\u5e9f\u9875\uff08\u6574\u9875\u5212\u7ebf / VOID / \u4f5c\u5e9f\uff09\uff0c\u5df2\u5f52\u4e3a\u300c\u5df2\u62d2\u7edd\u300d\uff0c\u4e0d\u8fdb\u7ed3\u7b97 / \u822a\u8ff9\u94fe\u3002\u5982\u7cfb\u8bef\u5224\uff0c\u53ef\u300c\u53d6\u6d88\u4f5c\u5e9f\u300d\u7ffb\u6848\u3002',
    unvoid: '\u53d6\u6d88\u4f5c\u5e9f',
    unvoidConfirmTitle: '\u53d6\u6d88\u672c\u9875\u7684\u4f5c\u5e9f\u6807\u8bb0\uff1f',
    unvoidConfirmDesc: '\u5c06\u6e05\u9664\u4f5c\u5e9f\u6807\u8bb0\u5e76\u4ece\u300c\u5df2\u62d2\u7edd\u300d\u590d\u6d3b\uff0c\u8bb0\u5f55\u91cd\u65b0\u53c2\u4e0e\u5bf9\u9f50\uff1b\u4eba\u5de5\u5df2\u6539\u5b57\u6bb5\u4fdd\u7559\u3002',
    unvoidSuccess: '\u5df2\u53d6\u6d88\u4f5c\u5e9f',
    unvoidFailed: '\u53d6\u6d88\u4f5c\u5e9f\u5931\u8d25',
    // 已拒绝（重复被取代）记录：隐藏保存/确认复核，引导先「设为主记录」翻案。
    rejectedReviewHint: '\u8be5\u8bb0\u5f55\u5df2\u62d2\u7edd\uff08\u91cd\u590d\u88ab\u53d6\u4ee3\uff09\uff0c\u9700\u590d\u6838\u8bf7\u5148\u300c\u8bbe\u4e3a\u4e3b\u8bb0\u5f55\u300d\u7ffb\u6848',
    // 已拒绝（作废页）记录：隐藏保存/确认复核，引导先「取消作废」翻案。
    voidedReviewHint: '\u8be5\u8bb0\u5f55\u4e3a\u4f5c\u5e9f\u9875\uff08\u5df2\u62d2\u7edd\uff09\uff0c\u9700\u590d\u6838\u8bf7\u5148\u300c\u53d6\u6d88\u4f5c\u5e9f\u300d\u7ffb\u6848',
};

// \u63a8\u7b97\u6765\u6e90\u4ee3\u7801 \u2192 \u4e2d\u6587\u8bf4\u660e\uff08\u201c\u63a8\u201d\u6807\u7b7e\u60ac\u6d6e\u63d0\u793a\u7528\uff09\u3002
const RECON_SOURCE_LABELS = {
    fleet_whitelist_snap: '\u673a\u53f7\u5438\u9644\u5230\u673a\u961f\u6210\u5458',
    back_calc: '\u540c\u5f20\u65f6\u95f4\u5012\u63a8',
    prev_leg_arrival: '\u53d6\u4e0a\u4e00\u6bb5\u5230\u8fbe\u5730',
    next_leg_departure: '\u53d6\u4e0b\u4e00\u6bb5\u51fa\u53d1\u5730',
    prev_leg_on_block_fuel: '\u53d6\u4e0a\u4e00\u6bb5\u5165\u4f4d\u6cb9\u91cf',
    next_leg_previous_fob: '\u53d6\u4e0b\u4e00\u6bb5\u52a0\u6cb9\u524d\u5b58\u91cf',
    flight_no_prefix: '\u822a\u73ed\u53f7\u524d\u7f00\u89c4\u8303\u5316',
    model_tool_adjust: '\u6a21\u578b\u5de5\u5177\u6838\u5bf9\u4fee\u6b63',
    airport_db: '\u673a\u573a\u4e3b\u6570\u636e\u67e5\u8868',
};

const STATUS_META = {
    pending: { label: TEXT.pending, color: 'default', icon: <ClockCircleOutlined /> },
    recognizing: { label: TEXT.recognizing, color: 'processing', icon: <FileSearchOutlined /> },
    parsed: { label: TEXT.parsed, color: 'success', icon: <CheckCircleOutlined /> },
    review_required: { label: TEXT.reviewRequired, color: 'warning', icon: <ExclamationCircleOutlined /> },
    failed: { label: TEXT.failed, color: 'error', icon: <ExclamationCircleOutlined /> },
    confirmed: { label: TEXT.confirmed, color: 'green', icon: <CheckCircleOutlined /> },
    rejected: { label: TEXT.rejectedStatus, color: 'default', icon: <ExclamationCircleOutlined /> },
};

const statusOptions = Object.entries(STATUS_META).map(([value, meta]) => ({
    value,
    label: meta.label,
}));

// T14: \u8bc6\u522b\u9875 "\u6765\u6e90" \u8868\u793a\u3002
//   manual_upload  \u9ed8\u8ba4\u8272 = \u4eba\u5de5\u4e0a\u4f20\uff08\u5386\u53f2 / \u4e3b\u6d41\u8def\u5f84\uff09
//   external_crawl \u84dd\u8272      = \u91c7\u96c6\u5668\u81ea\u52a8\u63a5\u5165\uff08\u672c\u671f\u65b0\u589e\uff09
const SOURCE_TYPE_META = {
    manual_upload: { label: TEXT.sourceManualUpload, color: 'default' },
    external_crawl: { label: TEXT.sourceExternalCrawl, color: 'blue' },
};

const sourceTypeOptions = Object.entries(SOURCE_TYPE_META).map(([value, meta]) => ({
    value,
    label: meta.label,
}));

const getErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return detail?.message || err?.response?.data?.message || err.message;
};

const isSupportedFile = (file) => {
    const name = file?.name || '';
    const lowerName = name.toLowerCase();
    return ALLOWED_EXT.some((ext) => lowerName.endsWith(ext))
        || file?.type === 'application/pdf'
        || file?.type?.startsWith('image/');
};

const validateFile = (file) => {
    if (!isSupportedFile(file)) {
        return { ok: false, reason: TEXT.invalidFileType };
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        return { ok: false, reason: TEXT.invalidFileSize };
    }
    return { ok: true };
};

const formatTime = (value) => value || '-';
const { TextArea } = Input;

/**
 * 在浏览器端用 Web Crypto API 算文件 SHA256（hex 字符串）。
 *
 * 跟后端 ``hashlib.sha256(content).hexdigest()`` 完全一致，所以前后端
 * 算出来的 hash 才能对得上。
 *
 * 注意：``crypto.subtle`` 仅在 secure context（https / localhost）下可用。
 * 生产域名是 https 的话没问题；本地 localhost 也 OK；裸 http 内网部署会失败，
 * 这种场景会 throw，外层捕获后 fallback 到「直接上传 + 事后看 dup」的旧路径。
 */
const sha256HexFromFile = async (file) => {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

const isImageSource = (record) => {
    const contentType = record?.content_type || '';
    const fileName = (record?.file_name || '').toLowerCase();
    return contentType.startsWith('image/')
        || ['.gif', '.jpg', '.jpeg', '.png', '.webp'].some((ext) => fileName.endsWith(ext));
};

// 字段顺序对齐 9H-FLM MVT 结算 Excel（详见 backend/db_migrations/108）。
// 列表/抽屉/导出三处统一用这个顺序，避免业务侧再问"为啥不一样"。
const REVIEW_FIELDS = [
    ['flight_log_no', 'TL No./Flight Log No.'],
    ['msn', 'MSN'],
    // S1: A/C Reg 从输入框改为机队下拉（fleet 类型）
    ['aircraft_reg', 'A/C Reg', 'fleet'],
    ['flight_no', 'Flight No.'],
    ['date_utc', 'Date UTC'],
    ['off_chocks_utc', 'Off Chocks'],
    ['takeoff_utc', 'Takeoff'],
    ['landing_utc', 'Landing'],
    ['on_chocks_utc', 'On Chocks'],
    ['from_iata', 'From IATA'],
    ['from_icao', 'From ICAO'],
    ['to_iata', 'To IATA'],
    ['to_icao', 'To ICAO'],
    ['flight_hours', 'Flight Hours'],
    ['block_hours', 'Block Hours'],
    // 燃油 7 个字段，顺序 = MVT Excel
    ['off_block_fuel_kg', 'Fuel KG (Off Block)', 'number'],
    ['fuel_liter', 'Fuel Liter', 'number'],
    ['fuel_density', 'Fuel Density', 'number'],
    ['actual_uplift_kg', 'Fuel Uplift', 'number'],
    ['previous_fuel_onboard_kg', 'Previous Fuel Onboard', 'number'],
    ['fuel_check_kg', 'Fuel Check KG', 'number'],
    ['on_block_fuel_kg', 'On Block Fuel KG', 'number'],
    ['major_maintenance', TEXT.majorMaintenance, 'text'],
    ['incidents_observations', '故障 / 事件观察 (Incidents)', 'text'],
];

// 详情抽屉分区布局（12 列网格 span）：按单据物理顺序分组，方便左图右表对照复核
const REVIEW_FIELD_GROUPS = [
    {
        key: 'basic',
        title: '基础与航段信息',
        items: [
            ['flight_log_no', 'TL No./Flight Log No.', undefined, 4],
            ['aircraft_reg', 'A/C Reg', 'fleet', 4],
            ['msn', 'MSN', undefined, 4],
            ['flight_no', 'Flight No.', undefined, 6],
            ['date_utc', 'Date UTC', undefined, 6],
            ['from_iata', 'From IATA', undefined, 3],
            ['from_icao', 'From ICAO', undefined, 3],
            ['to_iata', 'To IATA', undefined, 3],
            ['to_icao', 'To ICAO', undefined, 3],
        ],
    },
    {
        key: 'times',
        title: '时刻与时长 (UTC)',
        items: [
            ['off_chocks_utc', 'Off Chocks', undefined, 3],
            ['takeoff_utc', 'Takeoff', undefined, 3],
            ['landing_utc', 'Landing', undefined, 3],
            ['on_chocks_utc', 'On Chocks', undefined, 3],
            ['block_hours', 'Block Hours', undefined, 6],
            ['flight_hours', 'Flight Hours', undefined, 6],
        ],
    },
    {
        key: 'fuel',
        title: '燃油数据',
        items: [
            ['previous_fuel_onboard_kg', 'Previous Fuel Onboard', 'number', 4],
            ['actual_uplift_kg', 'Fuel Uplift', 'number', 4],
            ['off_block_fuel_kg', 'Fuel KG (Off Block)', 'number', 4],
            ['on_block_fuel_kg', 'On Block Fuel KG', 'number', 3],
            ['fuel_liter', 'Fuel Liter', 'number', 3],
            ['fuel_density', 'Fuel Density', 'number', 3],
            ['fuel_check_kg', 'Fuel Check KG', 'number', 3],
        ],
    },
    {
        key: 'maintenance',
        title: '维修记录与复核备注',
        items: [
            ['major_maintenance', TEXT.majorMaintenance, 'text', 6],
            ['incidents_observations', '故障 / 事件观察 (Incidents)', 'text', 6],
        ],
        includeReviewNotes: true,
    },
];

const FIELD_LABEL_MAP = Object.fromEntries(REVIEW_FIELDS.map(([name, label]) => [name, label]));

const buildReviewInitialValues = (record = {}) => {
    const values = {};
    REVIEW_FIELDS.forEach(([name]) => {
        values[name] = record[name] ?? record.extracted_data?.[name] ?? record.recognition_result?.[name] ?? null;
    });
    values.review_notes = record.review_notes || '';
    return values;
};

const getReviewReasonText = (record = {}) => {
    if (record.review_reason_text) return record.review_reason_text;
    const reasons = Array.isArray(record.review_reasons) ? record.review_reasons : [];
    if (!reasons.length) return '';
    return reasons.map((reason) => reason.message || reason.field_label || reason.field || reason.code).filter(Boolean).join('\uff1b');
};

const getToolTrace = (record = {}) => (
    record.recognition_result?._tool_trace
    || record.extracted_data?._tool_trace
    || null
);

// S1-S5: \u8bfb\u53d6\u67d0\u5b57\u6bb5\u7684\u63a8\u7b97\u6765\u6e90\u6807\u8bb0\uff08recognition_result._reconciliation[field]\uff09\u3002
const getFieldMark = (record = {}, field) => {
    const recon = record.recognition_result?._reconciliation;
    if (!recon || typeof recon !== 'object') return null;
    const mark = recon[field];
    return mark && typeof mark === 'object' ? mark : null;
};

// \u201c\u63a8\u201d\u6807\u7b7e\u7684\u60ac\u6d6e\u63d0\u793a\u6587\u672c\uff1a\u6765\u6e90\u7c7b\u578b + \u4f9d\u636e\u7b97\u5f0f / \u539f\u503c / \u6765\u6e90\u8bb0\u5f55 ID\u3002
const buildMarkTooltip = (mark) => {
    if (!mark) return '';
    const parts = [RECON_SOURCE_LABELS[mark.source] || mark.source];
    if (mark.from) parts.push(`\u539f\u503c: ${mark.from}`);
    if (mark.basis) parts.push(`\u4f9d\u636e: ${mark.basis}`);
    if (mark.source_record_id) parts.push(`\u6765\u6e90\u8bb0\u5f55: ${mark.source_record_id}`);
    if (Array.isArray(mark.flags) && mark.flags.length) parts.push(`\u6807\u8bb0: ${mark.flags.join(', ')}`);
    return parts.join('\uff1b');
};

// T15: \u5217\u8868\u5355\u5143\u683c\u6e32\u67d3 \u2014\u2014 \u5e26 _reconciliation \u6807\u8bb0\u7684\u5b57\u6bb5\u5728\u503c\u65c1\u8ddf\u4e00\u4e2a\u201c\u63a8\u201d\u6807\u7b7e\uff08\u60ac\u6d6e\u663e\u793a\u6765\u6e90\uff09\uff0c
// \u4e0e\u8be6\u60c5\u62bd\u5c49\u98ce\u683c\u4e00\u81f4\uff1b\u672a\u786e\u8ba4\u5b57\u6bb5\u8ffd\u52a0\u201c\u672a\u786e\u8ba4\u201d\u6807\u8bb0\u3002
const renderWithMark = (content, record, field) => {
    const mark = getFieldMark(record, field);
    const unconfirmed = isFieldUnconfirmed(record, field);
    if (!mark && !unconfirmed) return content;
    return (
        <Space size={4}>
            <span>{content}</span>
            {mark && (
                <Tooltip title={buildMarkTooltip(mark)}>
                    <Tag color="purple" style={{ margin: 0, lineHeight: '16px', padding: '0 4px' }}>
                        {TEXT.derivedTag}
                    </Tag>
                </Tooltip>
            )}
            {unconfirmed && <UnconfirmedFieldBadge record={record} field={field} />}
        </Space>
    );
};


const FlightLogRecognitionPanel = () => {
    const [uploading, setUploading] = useState(false);
    const [uploadResultOpen, setUploadResultOpen] = useState(false);
    // uploadResult 持久化到 sessionStorage：关弹窗后还可以点「上次上传结果」按钮重新打开。
    const [uploadResult, setUploadResultRaw] = useState(() => {
        try {
            const cached = sessionStorage.getItem(UPLOAD_RESULT_STORAGE_KEY);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const setUploadResult = useCallback((result) => {
        setUploadResultRaw(result);
        try {
            if (result) {
                sessionStorage.setItem(UPLOAD_RESULT_STORAGE_KEY, JSON.stringify(result));
            } else {
                sessionStorage.removeItem(UPLOAD_RESULT_STORAGE_KEY);
            }
        } catch {
            // 隐私模式 / 配额满 等场景下 sessionStorage 不可用，忽略即可。
        }
    }, []);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [reviewSaving, setReviewSaving] = useState(false);
    const [notModelError, setNotModelError] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [sourcePreview, setSourcePreview] = useState(null);
    const [reviewForm] = Form.useForm();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingSource, setExportingSource] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    // 去重翻案进行中的记录 id（按钮 loading 用）。
    const [dedupRestoringId, setDedupRestoringId] = useState(null);
    const [unvoidingId, setUnvoidingId] = useState(null);
    // S1: 在役机队选项（机号下拉）。取空时降级为只读展示。
    const [fleetOptions, setFleetOptions] = useState([]);
    const [tableScrollY, setTableScrollY] = useState(() => {
        if (typeof window === 'undefined') return 400;
        const h = window.innerHeight - TABLE_SCROLL_FIXED_OFFSET;
        return h > 200 ? h : 400;
    });

    // 「上传预览」相关：拖文件后先本地算 SHA256 → 查重 → 让用户看清楚再决定上不上。
    const [preCheckOpen, setPreCheckOpen] = useState(false);
    const [preCheckLoading, setPreCheckLoading] = useState(false);
    const [preCheckBatch, setPreCheckBatch] = useState([]);

    const pendingBatchRef = useRef([]);
    const warnedTruncateRef = useRef(false);

    const handleClearSelection = useCallback(() => {
        setSelectedRowKeys([]);
    }, []);

    const handleListLoadError = useCallback((err) => {
        message.error(`${TEXT.listLoadFailed}: ${getErrorMessage(err)}`);
    }, []);

    const handleInFlightUpdated = useCallback((updatedItem) => {
        if (!updatedItem?.id) return;
        setSelectedRecord((prev) => (prev?.id === updatedItem.id ? { ...prev, ...updatedItem } : prev));
    }, []);

    const {
        filters,
        setFilters,
        activeTab,
        aircraftTabs,
        data,
        total,
        loading,
        loadingMore,
        loadMoreError,
        hasMore,
        chainIsComputing,
        chainOrderStale,
        recomputingChains,
        tableContainerRef,
        fetchInitial,
        loadMore,
        refreshLoadedRange,
        recomputeChains,
        selectTab,
        applyFilterPatch,
        submitSearch,
        resetFilters,
        replaceRecord,
        markRecordsAsPlaceholder,
        getCurrentScopeParams,
    } = useFlightLogInfiniteScroll({
        onClearSelection: handleClearSelection,
        onListLoadError: handleListLoadError,
        onInFlightRecordUpdated: handleInFlightUpdated,
    });

    // S1: 加载在役机队选项（机号下拉）。失败 / 为空时静默降级为只读。
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await flightLogRecognitionApi.fleetOptions();
                const result = res.data || res;
                if (!cancelled) setFleetOptions(result.items || []);
            } catch (_) {
                if (!cancelled) setFleetOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const updateHeight = () => {
            const h = window.innerHeight - TABLE_SCROLL_FIXED_OFFSET;
            setTableScrollY(h > 200 ? h : 400);
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleSearch = submitSearch;
    const applyFilter = applyFilterPatch;
    const handleReset = resetFilters;

    /**
     * 真正的上传调用：仅在用户从「预检查 Modal」点了「上传」后触发，
     * 或者预检查失败 fallback 时直接调用。
     */
    const doActualUpload = useCallback(async (files) => {
        if (!files.length) return;
        setUploading(true);
        try {
            const res = await flightLogRecognitionApi.upload(files);
            const result = res.data || res;
            setUploadResult(result);
            setUploadResultOpen(true);
            message.success(TEXT.uploadSuccess);
            fetchInitial({ resetScroll: true, clearPlaceholders: false, refreshTabs: true });
        } catch (err) {
            message.error(`${TEXT.uploadFailed}: ${getErrorMessage(err)}`);
        } finally {
            setUploading(false);
        }
    }, [fetchInitial, setUploadResult]);

    /**
     * 拖入文件后的入口：先本地算 SHA256 → 调 check-existence → 弹「上传预览」让用户确认。
     *
     * 容错：crypto.subtle 在非 secure context（裸 http）下不可用，
     * 或者 check-existence 接口异常，都 fallback 到「直接传 + 事后看 dup」的旧路径，
     * 保证可用性兜底。
     */
    const handleUploadSubmit = async (rawFiles) => {
        if (!rawFiles.length) return;
        const files = rawFiles.slice(0, MAX_FILES_PER_BATCH);

        if (typeof crypto === 'undefined' || !crypto?.subtle) {
            await doActualUpload(files);
            return;
        }

        setPreCheckLoading(true);
        setPreCheckOpen(true);
        try {
            const hashed = await Promise.all(
                files.map(async (file) => ({ file, hash: await sha256HexFromFile(file) }))
            );
            const res = await flightLogRecognitionApi.checkExistence(hashed.map((h) => h.hash));
            const result = res.data || res;
            const byHash = new Map((result.items || []).map((it) => [it.hash, it]));
            const merged = hashed.map(({ file, hash }) => {
                const found = byHash.get(hash) || { exists: false, record: null };
                return { file, hash, exists: !!found.exists, record: found.record };
            });
            setPreCheckBatch(merged);
        } catch (err) {
            message.warning(`${TEXT.preCheckFailed}: ${getErrorMessage(err)}`);
            setPreCheckOpen(false);
            setPreCheckBatch([]);
            await doActualUpload(files);
        } finally {
            setPreCheckLoading(false);
        }
    };

    const beforeUpload = (file, fileList) => {
        if (uploading) return Upload.LIST_IGNORE;

        if (fileList.length > MAX_FILES_PER_BATCH && !warnedTruncateRef.current) {
            message.warning(TEXT.batchTruncated);
            warnedTruncateRef.current = true;
        }

        const result = validateFile(file);
        if (result.ok) {
            pendingBatchRef.current.push(file);
        } else {
            message.error(`${file.name}: ${result.reason}`);
        }

        if (file === fileList[fileList.length - 1]) {
            const batch = pendingBatchRef.current;
            pendingBatchRef.current = [];
            warnedTruncateRef.current = false;
            handleUploadSubmit(batch);
        }

        return Upload.LIST_IGNORE;
    };

    // 详情请求序号。后发出的打开、关闭抽屉、离开本页签都会让旧请求作废，
    // 避免慢返回把当前正在看的另一条记录或已填的复核内容盖回去。
    const detailRequestRef = useRef(0);
    // 地址栏 detail_id 已经自动打开过的记录。同一条不重复打开，避免把正在改的内容重置掉。
    const urlDetailOpenedRef = useRef(null);

    const handleOpenDetail = useCallback(async (record) => {
        const requestId = ++detailRequestRef.current;
        setSelectedRecord(record);
        setSourcePreview(null);
        setNotModelError(false);
        setDetailOpen(true);
        if (!record?.id) return;

        setDetailLoading(true);
        try {
            const [detailRes, previewRes] = await Promise.all([
                flightLogRecognitionApi.detail(record.id),
                flightLogRecognitionApi.signedUrl(record.id),
            ]);
            if (requestId !== detailRequestRef.current) return;
            const detail = detailRes.data || detailRes;
            setSelectedRecord(detail);
            reviewForm.setFieldsValue(buildReviewInitialValues(detail));
            setSourcePreview(previewRes.data || previewRes);
        } catch (err) {
            if (requestId !== detailRequestRef.current) return;
            message.error(`${TEXT.detailLoadFailed}: ${getErrorMessage(err)}`);
        } finally {
            if (requestId === detailRequestRef.current) {
                setDetailLoading(false);
            }
        }
    }, [reviewForm]);

    // 抓取页标签跳转带 detail_id 参数 → 直接打开该识别记录的详情 Drawer。
    // 关闭 Drawer 或切换离开 flight-log Tab 时清理，避免残留参数反复弹出。
    const [searchParams, setSearchParams] = useSearchParams();
    const detailIdFromUrl = searchParams.get('detail_id');
    const tabFromUrl = searchParams.get('tab');
    useEffect(() => {
        if (tabFromUrl !== 'flight-log') {
            detailRequestRef.current += 1;
            urlDetailOpenedRef.current = null;
            setDetailOpen(false);
            setDetailLoading(false);
            return;
        }
        if (!detailIdFromUrl) {
            urlDetailOpenedRef.current = null;
            return;
        }
        if (urlDetailOpenedRef.current === detailIdFromUrl) return;
        urlDetailOpenedRef.current = detailIdFromUrl;
        handleOpenDetail({ id: detailIdFromUrl });
    }, [detailIdFromUrl, tabFromUrl, handleOpenDetail]);
    const clearDetailIdParam = () => {
        if (!searchParams.get('detail_id')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('detail_id');
        setSearchParams(next, { replace: true });
    };

    const handleReviewSubmit = async (confirm = false, acceptUnconfirmed = false) => {
        if (!selectedRecord?.id) return;

        // D13: 整单确认时若仍存在未确认字段，必须弹窗提示批量确认
        const unconfirmedList = Array.isArray(selectedRecord.unconfirmed_fields)
            ? selectedRecord.unconfirmed_fields
            : [];
        if (confirm && !acceptUnconfirmed && unconfirmedList.length > 0) {
            const labels = unconfirmedList.map((f) => FIELD_LABEL_MAP[f] || f).join('、');
            Modal.confirm({
                title: '存在未逐一确认的预填/低置信字段',
                icon: <ExclamationCircleOutlined />,
                content: `当前记录仍有 ${unconfirmedList.length} 个未确认字段（${labels}）。点击「一并确认」将批量采纳上述预填值并将整单标记为已确认。`,
                okText: '一并确认',
                cancelText: '取消',
                onOk: () => handleReviewSubmit(true, true),
            });
            return;
        }

        setReviewSaving(true);
        try {
            const values = await reviewForm.validateFields();
            const { review_notes: reviewNotes, ...fields } = values;
            const res = await flightLogRecognitionApi.review(selectedRecord.id, {
                confirm,
                review_notes: reviewNotes,
                fields,
                not_model_error: notModelError,
                accept_unconfirmed: acceptUnconfirmed,
            });
            const nextRecord = res.data || res;
            setSelectedRecord(nextRecord);
            setNotModelError(false);
            reviewForm.setFieldsValue(buildReviewInitialValues(nextRecord));
            message.success(confirm ? TEXT.reviewConfirmSuccess : TEXT.reviewSaveSuccess);
            replaceRecord(nextRecord);
            refreshLoadedRange();
        } catch (err) {
            if (err?.errorFields) return;
            message.error(`${TEXT.reviewFailed}: ${getErrorMessage(err)}`);
        } finally {
            setReviewSaving(false);
        }
    };

    const handleFieldAdjustmentDecision = useCallback(async (field, decision, isNotModelError = false) => {
        if (!selectedRecord?.id) return;
        try {
            const res = await flightLogRecognitionApi.decideFieldAdjustment(selectedRecord.id, field, {
                decision,
                not_model_error: isNotModelError,
            });
            const nextRecord = res.data || res;
            setSelectedRecord(nextRecord);
            reviewForm.setFieldsValue(buildReviewInitialValues(nextRecord));
            replaceRecord(nextRecord);
            refreshLoadedRange();
            message.success(decision === 'accepted' ? '已采纳模型预填值' : '已撤回至原读值');
        } catch (err) {
            message.error(`处理失败: ${getErrorMessage(err)}`);
        }
    }, [selectedRecord?.id, reviewForm, replaceRecord, refreshLoadedRange]);

    const handleReextractSuggestionsDecision = useCallback(async (fields, decision) => {
        if (!selectedRecord?.id || !fields?.length) return;
        try {
            const res = await flightLogRecognitionApi.decideReextractSuggestions(selectedRecord.id, {
                fields,
                decision,
            });
            const nextRecord = res.data || res;
            setSelectedRecord(nextRecord);
            reviewForm.setFieldsValue(buildReviewInitialValues(nextRecord));
            replaceRecord(nextRecord);
            if (decision === 'accepted' && nextRecord.page_voided) {
                markRecordsAsPlaceholder(
                    [nextRecord.id],
                    '该记录已标记为整页作废，切换页签或刷新后重排',
                    { ...nextRecord, decrementTotal: false }
                );
            }
            refreshLoadedRange();
            message.success(decision === 'accepted' ? '已采纳重识别差异建议' : '已驳回/忽略建议');
        } catch (err) {
            message.error(`处理重识别建议失败: ${getErrorMessage(err)}`);
        }
    }, [selectedRecord?.id, reviewForm, replaceRecord, markRecordsAsPlaceholder, refreshLoadedRange]);

    const handleReExtract = async (record) => {
        if (!record?.id) return;
        try {
            await flightLogRecognitionApi.reExtract(record.id);
            message.success(TEXT.reExtractQueued);
            try {
                const detailRes = await flightLogRecognitionApi.detail(record.id);
                const nextRecord = detailRes.data || detailRes;
                replaceRecord(nextRecord);
                setSelectedRecord((prev) => (prev?.id === nextRecord.id ? nextRecord : prev));
                refreshLoadedRange();
            } catch (_) {
                // 拿不到最新 detail 不影响主流程
            }
        } catch (err) {
            message.error(`${TEXT.reExtractFailed}: ${getErrorMessage(err)}`);
        }
    };

    // 人工翻案：把被取代记录改选为去重主记录（清取代标记 + 复活 + 取代同组其余）。
    const handleDedupRestore = useCallback(async (record) => {
        if (!record?.id) return;
        setDedupRestoringId(record.id);
        try {
            const res = await flightLogRecognitionApi.dedupRestore(record.id);
            const nextRecord = res.data || res;
            const supersededIds = Array.from(new Set(nextRecord.dedup_promote?.superseded_ids || []));
            replaceRecord(nextRecord);
            if (supersededIds.length > 0) {
                markRecordsAsPlaceholder(
                    supersededIds,
                    '该记录已被去重取代，切换页签或刷新后重排',
                    {
                        status: 'rejected',
                        dedup_superseded: true,
                        superseded_by: nextRecord.id,
                        dedup_manual_main: false,
                        decrementTotal: false,
                    }
                );
            }
            refreshLoadedRange();
            setSelectedRecord((prev) => (prev?.id === nextRecord.id ? nextRecord : prev));
            message.success(TEXT.dedupRestoreSuccess);
        } catch (err) {
            message.error(`${TEXT.dedupRestoreFailed}: ${getErrorMessage(err)}`);
        } finally {
            setDedupRestoringId(null);
        }
    }, [markRecordsAsPlaceholder, refreshLoadedRange, replaceRecord]);

    // 作废页人工翻案「取消作废」：清 page_voided 标记 + 从 rejected 复活，并刷新链序。
    const handleUnvoid = useCallback(async (record) => {
        if (!record?.id) return;
        setUnvoidingId(record.id);
        try {
            const res = await flightLogRecognitionApi.unvoid(record.id);
            const nextRecord = res.data || res;
            replaceRecord(nextRecord);
            refreshLoadedRange();
            setSelectedRecord((prev) => (prev?.id === nextRecord.id ? nextRecord : prev));
            message.success(TEXT.unvoidSuccess);
        } catch (err) {
            message.error(`${TEXT.unvoidFailed}: ${getErrorMessage(err)}`);
        } finally {
            setUnvoidingId(null);
        }
    }, [refreshLoadedRange, replaceRecord]);

    const handleDelete = async (record) => {
        if (!record?.id) return;
        try {
            await flightLogRecognitionApi.remove(record.id);
            message.success(TEXT.deleteSuccess);
            if (selectedRecord?.id === record.id) {
                setDetailOpen(false);
                setSelectedRecord(null);
            }
            setSelectedRowKeys((prev) => prev.filter((id) => id !== record.id));
            // D16 / T11③: 转为灰色占位行保留在当前位置，切换页签或刷新后清除
            markRecordsAsPlaceholder([record.id], '该记录已删除，切换页签或刷新后移除');
            refreshLoadedRange();
        } catch (err) {
            message.error(`${TEXT.deleteFailed}: ${getErrorMessage(err)}`);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) return;
        setBatchDeleting(true);
        try {
            const res = await flightLogRecognitionApi.batchRemove(selectedRowKeys);
            const result = res.data || res;
            const deleted = result.deleted_count ?? 0;
            const failed = result.failed_count ?? 0;
            if (failed > 0) {
                message.warning(TEXT.batchDeletePartial(deleted, failed));
            } else {
                message.success(TEXT.batchDeleteSuccess(deleted));
            }
            if (selectedRecord && selectedRowKeys.includes(selectedRecord.id)) {
                setDetailOpen(false);
                setSelectedRecord(null);
            }
            const deletedIds = (result.items || [])
                .filter((it) => it?.status === 'deleted')
                .map((it) => it.id);
            markRecordsAsPlaceholder(
                deletedIds.length ? deletedIds : selectedRowKeys,
                '该记录已删除，切换页签或刷新后移除'
            );
            setSelectedRowKeys([]);
            refreshLoadedRange();
        } catch (err) {
            message.error(`${TEXT.batchDeleteFailed}: ${getErrorMessage(err)}`);
        } finally {
            setBatchDeleting(false);
        }
    };

    const triggerDownload = (downloadUrl, fileName) => {
        if (!downloadUrl) return;
        const link = document.createElement('a');
        link.href = downloadUrl;
        if (fileName) link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // S7 & T14: 手动“对齐/补全”。传入当前二级页签上下文 view / exact_reg（D55、D56）。
    const handleReconcile = async () => {
        setReconciling(true);
        try {
            const scopeParams = getCurrentScopeParams();
            const payload = selectedRowKeys.length > 0
                ? { ids: selectedRowKeys, view: scopeParams.view, exact_reg: scopeParams.exact_reg }
                : scopeParams;
            const res = await flightLogRecognitionApi.reconcile(payload);
            const result = res.data || res;
            const changed = result.changed_count ?? 0;
            message.success(changed > 0 ? TEXT.reconcileSuccess(changed) : TEXT.reconcileNoChange);
            refreshLoadedRange();
        } catch (err) {
            message.error(`${TEXT.reconcileFailed}: ${getErrorMessage(err)}`);
        } finally {
            setReconciling(false);
        }
    };

    const handleExportFiltered = async () => {
        if (total === 0) {
            message.warning(TEXT.exportEmpty);
            return;
        }
        setExporting(true);
        try {
            const res = await flightLogRecognitionApi.exportFiltered(getCurrentScopeParams());
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(`${TEXT.exportSuccess}: ${result.count ?? '?'}`);
        } catch (err) {
            message.error(`${TEXT.exportFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportBatch = async () => {
        if (selectedRowKeys.length === 0) return;
        setExporting(true);
        try {
            const scopeParams = getCurrentScopeParams();
            const res = await flightLogRecognitionApi.exportBatch(selectedRowKeys, {
                view: scopeParams.view,
                exact_reg: scopeParams.exact_reg,
            });
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(`${TEXT.exportSuccess}: ${result.count ?? selectedRowKeys.length}`);
        } catch (err) {
            message.error(`${TEXT.exportFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportSourceFiles = async (ids) => {
        const targetIds = Array.isArray(ids) && ids.length > 0 ? ids : selectedRowKeys;
        if (!targetIds.length) return;
        setExportingSource(true);
        try {
            const res = await flightLogRecognitionApi.exportSourceFiles(targetIds);
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(TEXT.exportSourceSuccess);
        } catch (err) {
            message.error(`${TEXT.exportSourceFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExportingSource(false);
        }
    };

    const rowMetaMap = useMemo(() => buildChainRowMetaMap(data, { view: activeTab.view, chainIsComputing }), [data, activeTab.view, chainIsComputing]);
    const dataByIdMap = useMemo(() => new Map(data.filter((r) => r?.id).map((r) => [String(r.id), r])), [data]);
    const tableComponents = useMemo(() => ({ body: { row: createFlightLogBodyRow(rowMetaMap, dataByIdMap) } }), [rowMetaMap, dataByIdMap]);

    // T11①: 点行打开右侧核对抽屉（排除勾选框列、操作列及交互控件）
    const handleRowClick = useCallback((record, event) => {
        if (!record?.id || record.isPlaceholderRow) return;
        const target = event?.target;
        if (target?.closest?.('.ant-table-selection-column, .ant-checkbox-wrapper, button, a, input, .ant-popover')) return;
        handleOpenDetail(record);
    }, [handleOpenDetail]);

    const columns = useMemo(() => [
        {
            title: TEXT.status,
            dataIndex: 'status',
            width: 116,
            fixed: 'left',
            render: (status = 'pending', row) => {
                if (row.isPlaceholderRow) {
                    return <FlightLogChainStatusBadges record={row} view={activeTab.view} />;
                }
                const meta = STATUS_META[status] || STATUS_META.pending;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>
                            {meta.label}
                        </Tag>
                        {row.dedup_superseded && (
                            <Tooltip title={TEXT.dedupTagTip}>
                                <Tag color="gold" style={{ margin: 0 }}>{TEXT.dedupTagShort}</Tag>
                            </Tooltip>
                        )}
                        {row.page_voided && (
                            <Tooltip title={TEXT.voidedTagTip}>
                                <Tag color="red" style={{ margin: 0 }}>{TEXT.voidedTagShort}</Tag>
                            </Tooltip>
                        )}
                        <FlightLogChainStatusBadges record={row} view={activeTab.view} />
                    </Space>
                );
            },
        },
        {
            title: TEXT.sourceColumn,
            dataIndex: 'source_type',
            width: 96,
            fixed: 'left',
            render: (sourceType) => {
                const meta = SOURCE_TYPE_META[sourceType] || SOURCE_TYPE_META.manual_upload;
                return (
                    <Tag color={meta.color} style={{ margin: 0 }}>
                        {meta.label}
                    </Tag>
                );
            },
        },
        {
            title: TEXT.reviewReason,
            dataIndex: 'review_reason_text',
            width: 180,
            ellipsis: true,
            render: (value, row) => {
                if (row.isPlaceholderRow) {
                    return (
                        <Tooltip title={row.placeholderText}>
                            <Text type="secondary" ellipsis style={{ maxWidth: 160 }}>
                                {row.placeholderText}
                            </Text>
                        </Tooltip>
                    );
                }
                if (row.status !== 'review_required') return '-';
                const text = value || getReviewReasonText(row) || TEXT.noReviewReason;
                return (
                    <Tooltip title={text}>
                        <Text type="warning" ellipsis style={{ maxWidth: 160 }}>
                            {text}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            // T13: 取消列头点击排序，固定按服务端默认升序展示（D1、D10、D18）
            title: 'TL No.',
            dataIndex: 'flight_log_no',
            key: 'flight_log_no',
            width: 120,
            fixed: 'left',
            render: (value) => value || '-',
        },
        {
            title: TEXT.sourceFile,
            dataIndex: 'file_name',
            width: 220,
            ellipsis: true,
            render: (value, row) => (
                <Space size={6}>
                    {(row.content_type || value || '').includes('image') ? (
                        <FileImageOutlined style={{ color: '#1677ff' }} />
                    ) : (
                        <FilePdfOutlined style={{ color: '#d4380d' }} />
                    )}
                    <Text ellipsis style={{ maxWidth: 180 }}>
                        {value || '-'}
                    </Text>
                </Space>
            ),
        },
        // ─── Excel 字段顺序 (9H-FLM MVT) ───
        { title: 'MSN', dataIndex: 'msn', width: 100, render: (value, row) => renderWithMark(value || row.aircraft_reg || '-', row, 'aircraft_reg') },
        { title: 'Flight No.', dataIndex: 'flight_no', width: 110, render: (value, row) => renderWithMark(value || '-', row, 'flight_no') },
        {
            // T13: 取消列头点击排序
            title: 'Date UTC',
            dataIndex: 'date_utc',
            key: 'date_utc',
            width: 120,
            render: (value) => value || '-',
        },
        {
            // T13: 取消列头点击排序
            title: 'Off Chocks',
            dataIndex: 'off_chocks_utc',
            key: 'off_chocks_utc',
            width: 110,
            render: (value, row) => renderWithMark(formatTime(value), row, 'off_chocks_utc'),
        },
        { title: 'Takeoff', dataIndex: 'takeoff_utc', width: 96, render: (value, row) => renderWithMark(formatTime(value), row, 'takeoff_utc') },
        { title: 'Landing', dataIndex: 'landing_utc', width: 96, render: (value, row) => renderWithMark(formatTime(value), row, 'landing_utc') },
        { title: 'On Chocks', dataIndex: 'on_chocks_utc', width: 110, render: (value, row) => renderWithMark(formatTime(value), row, 'on_chocks_utc') },
        { title: 'From IATA', dataIndex: 'from_iata', width: 96, render: (value, row) => renderWithMark(value || '-', row, 'from_iata') },
        { title: 'From ICAO', dataIndex: 'from_icao', width: 96, render: (value, row) => renderWithMark(value || '-', row, 'from_icao') },
        { title: 'To IATA', dataIndex: 'to_iata', width: 96, render: (value, row) => renderWithMark(value || '-', row, 'to_iata') },
        { title: 'To ICAO', dataIndex: 'to_icao', width: 96, render: (value, row) => renderWithMark(value || '-', row, 'to_icao') },
        { title: 'Flight Hours', dataIndex: 'flight_hours', width: 110, render: (value) => value || '-' },
        { title: 'Block Hours', dataIndex: 'block_hours', width: 110, render: (value) => value || '-' },
        {
            title: 'Fuel',
            children: [
                { title: 'Fuel KG', dataIndex: 'off_block_fuel_kg', width: 110, render: (value) => value ?? '-' },
                { title: 'Fuel Liter', dataIndex: 'fuel_liter', width: 110, render: (value) => value ?? '-' },
                { title: 'Fuel Density', dataIndex: 'fuel_density', width: 110, render: (value) => value ?? '-' },
                { title: 'Fuel Uplift', dataIndex: 'actual_uplift_kg', width: 120, render: (value) => value ?? '-' },
                { title: 'Previous Fuel Onboard', dataIndex: 'previous_fuel_onboard_kg', width: 150, render: (value, row) => renderWithMark(value ?? '-', row, 'previous_fuel_onboard_kg') },
                { title: 'Fuel Check KG', dataIndex: 'fuel_check_kg', width: 130, render: (value) => value ?? '-' },
            ],
        },
        {
            title: 'Major Maintenance',
            dataIndex: 'major_maintenance',
            width: 220,
            ellipsis: true,
            render: (value) => value || '/',
        },
        {
            title: TEXT.confidence,
            dataIndex: 'extraction_confidence',
            width: 90,
            render: (value) => (value == null ? '-' : `${Math.round(value * 100)}%`),
        },
        {
            title: TEXT.actions,
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => {
                if (record.isPlaceholderRow) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Space size={4} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={TEXT.view}>
                            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(record)}>
                                {TEXT.view}
                            </Button>
                        </Tooltip>
                        <Popconfirm
                            title="确认重新识别该单据？"
                            description="重新识别将保留人工已复核修改的字段；若记录已确认或存在人工字段，模型新识别出的差异将作为待决建议展示，不会直接覆盖当前值。"
                            okText={TEXT.reExtract}
                            cancelText={TEXT.deleteCancel}
                            onConfirm={() => handleReExtract(record)}
                        >
                            <Button type="link" size="small" icon={<ReloadOutlined />}>
                                {TEXT.reExtract}
                            </Button>
                        </Popconfirm>

                        {record.dedup_superseded && (
                            <Popconfirm
                                title={TEXT.dedupRestoreConfirmTitle}
                                description={TEXT.dedupRestoreConfirmDesc}
                                okText={TEXT.dedupRestore}
                                cancelText={TEXT.deleteCancel}
                                onConfirm={() => handleDedupRestore(record)}
                            >
                                <Button type="link" size="small" loading={dedupRestoringId === record.id}>
                                    {TEXT.dedupRestore}
                                </Button>
                            </Popconfirm>
                        )}
                        {record.page_voided && (
                            <Popconfirm
                                title={TEXT.unvoidConfirmTitle}
                                description={TEXT.unvoidConfirmDesc}
                                okText={TEXT.unvoid}
                                cancelText={TEXT.deleteCancel}
                                onConfirm={() => handleUnvoid(record)}
                            >
                                <Button type="link" size="small" loading={unvoidingId === record.id}>
                                    {TEXT.unvoid}
                                </Button>
                            </Popconfirm>
                        )}
                        <Popconfirm
                            title={TEXT.deleteConfirmTitle}
                            description={TEXT.deleteConfirmDesc}
                            okText={TEXT.deleteOk}
                            cancelText={TEXT.deleteCancel}
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                                {TEXT.deleteAction}
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ], [selectedRecord?.id, dedupRestoringId, unvoidingId, activeTab.view]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderUploadResultItem = (item) => {
        const fileName = item.file_name || '-';
        const dup = item.duplicate === true;
        const rejected = !dup && item.status === 'rejected';
        const itemStyle = dup
            ? { background: '#fffbe6', borderLeft: '3px solid #faad14', paddingLeft: 8 }
            : undefined;
        const originalName = item.original_file_name;
        const originalUploadedAt = item.original_uploaded_at;
        // sv-SE locale 输出 ISO 风格 "YYYY-MM-DD HH:mm:ss"，截取到分钟。
        // 不引入 dayjs，原生 Date 已经够用。
        const formattedUploadedAt = originalUploadedAt
            ? new Date(originalUploadedAt).toLocaleString('sv-SE', { hour12: false }).slice(0, 16)
            : null;
        return (
            <List.Item style={itemStyle}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Space wrap>
                        {dup ? (
                            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        ) : rejected ? (
                            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        ) : (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        )}
                        <Text>{fileName}</Text>
                        <Tag color={dup ? 'gold' : rejected ? 'red' : 'blue'}>
                            {dup ? TEXT.duplicate : rejected ? TEXT.rejected : (item.status || TEXT.accepted)}
                        </Tag>
                        {dup && item.status && (
                            <Tag color="default">{`${TEXT.originalStatus}: ${item.status}`}</Tag>
                        )}
                        {item.message && !dup && <Text type="secondary">{item.message}</Text>}
                    </Space>
                    {dup && (originalName || formattedUploadedAt) && (
                        <Space wrap style={{ paddingLeft: 22 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {TEXT.duplicateOf}: <Text style={{ fontSize: 12 }}>{originalName || '-'}</Text>
                                {formattedUploadedAt && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {`  (${TEXT.uploadedAt} ${formattedUploadedAt})`}
                                    </Text>
                                )}
                            </Text>
                            {item.id && (
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => {
                                        setUploadResultOpen(false);
                                        handleOpenDetail({ id: item.id });
                                    }}
                                >
                                    {TEXT.viewOriginal}
                                </Button>
                            )}
                        </Space>
                    )}
                </Space>
            </List.Item>
        );
    };

    const uploadItems = uploadResult?.items || [];

    return (
        <div style={{ padding: 24 }}>
            <Card size="small" styles={{ body: { padding: 16 } }}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <style>{'.flight-log-compact-dragger .ant-upload-btn { padding: 6px 12px !important; }'}</style>
                        <div className="flight-log-compact-dragger" style={{ flex: 1, minWidth: 320 }}>
                            <Dragger
                                name="files"
                                multiple
                                accept={ALLOWED_EXT.join(',')}
                                showUploadList={false}
                                disabled={uploading}
                                beforeUpload={beforeUpload}
                            >
                                <Space size={8} wrap style={{ justifyContent: 'center' }}>
                                    <InboxOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                                    <Text strong style={{ fontSize: 13 }}>{uploading ? TEXT.uploading : TEXT.uploadText}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{`（${TEXT.uploadHint}）`}</Text>
                                </Space>
                            </Dragger>
                        </div>
                        {uploadResult && (
                            <Space size={8} style={{ flexShrink: 0 }}>
                                <Button size="small" icon={<EyeOutlined />} onClick={() => setUploadResultOpen(true)}>
                                    {TEXT.showLastUploadResult}
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {`${TEXT.accepted} ${uploadResult.accepted_count ?? 0} / ${TEXT.duplicate} ${uploadResult.duplicate_count ?? 0} / ${TEXT.rejected} ${uploadResult.rejected_count ?? 0}`}
                                </Text>
                            </Space>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder={`TL No. / ${TEXT.flightNo} / ${TEXT.aircraftReg} / ${TEXT.airport} / ${TEXT.fileName}`}
                                value={filters.keyword}
                                onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
                                onPressEnter={handleSearch}
                                style={{ flex: '1 1 160px', minWidth: 140, maxWidth: 240 }}
                            />
                            <Select
                                allowClear
                                placeholder={TEXT.status}
                                options={statusOptions}
                                value={filters.status}
                                onChange={(value) => applyFilter({ status: value })}
                                style={{ width: 110, flexShrink: 0 }}
                            />
                            <Select
                                allowClear
                                placeholder={TEXT.sourceColumn}
                                options={sourceTypeOptions}
                                value={filters.source_type}
                                onChange={(value) => applyFilter({ source_type: value })}
                                style={{ width: 110, flexShrink: 0 }}
                            />
                            <Tooltip title={activeTab.view !== 'all' ? '当前按二级页签筛选机号' : TEXT.aircraftRegFilter}>
                                <Select
                                    // 机号筛选：仅在「全部」页签生效（D55）；始终占位不隐藏，避免切换页签时控件跳动。
                                    mode="tags"
                                    allowClear
                                    disabled={activeTab.view !== 'all'}
                                    placeholder={TEXT.aircraftReg}
                                    popupMatchSelectWidth={false}
                                    value={filters.aircraft_reg ? filters.aircraft_reg.split(',') : []}
                                    onChange={(values) => applyFilter({
                                        aircraft_reg: (values || []).map((value) => value.trim()).filter(Boolean).join(','),
                                    })}
                                    options={fleetOptions.map((reg) => ({ value: reg, label: reg }))}
                                    maxTagCount="responsive"
                                    tokenSeparators={[',', ' ']}
                                    style={{ width: 150, flexShrink: 0 }}
                                />
                            </Tooltip>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(value) => applyFilter({ dateRange: value })}
                                style={{ width: 220, flexShrink: 0 }}
                            />
                        </div>
                        <Space size={8} style={{ flexShrink: 0 }}>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                {TEXT.search}
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                {TEXT.reset}
                            </Button>
                        </Space>
                    </div>

                    <FlightLogAircraftTabs
                        activeTab={activeTab}
                        aircraftRegs={aircraftTabs.aircraftRegs}
                        hasUnassigned={aircraftTabs.hasUnassigned}
                        chainIsComputing={chainIsComputing}
                        chainOrderStale={chainOrderStale}
                        recomputingChains={recomputingChains}
                        onSelectTab={selectTab}
                        onRefresh={() => fetchInitial({ resetScroll: true, clearPlaceholders: true, refreshTabs: true })}
                        onRecomputeChains={recomputeChains}
                        reconcileAction={(
                            <Button
                                size="small"
                                icon={<ThunderboltOutlined />}
                                onClick={handleReconcile}
                                loading={reconciling}
                            >
                                {reconciling ? TEXT.reconciling : TEXT.reconcile}
                            </Button>
                        )}
                        exportAction={(
                            <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={handleExportFiltered}
                                loading={exporting}
                                disabled={total === 0}
                            >
                                {TEXT.exportFiltered}
                            </Button>
                        )}
                    />

                    {selectedRowKeys.length > 0 && (
                        <Space style={{
                            padding: '8px 12px',
                            background: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 4,
                            width: '100%',
                            justifyContent: 'space-between',
                        }}>
                            <Text>{TEXT.batchDeleteSelected(selectedRowKeys.length)}</Text>
                            <Space>
                                <Button
                                    size="small"
                                    onClick={() => setSelectedRowKeys([])}
                                    disabled={batchDeleting || exporting}
                                >
                                    {TEXT.clearSelection}
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportBatch}
                                    loading={exporting}
                                    disabled={batchDeleting}
                                >
                                    {TEXT.exportBatch}
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleExportSourceFiles(selectedRowKeys)}
                                    loading={exportingSource}
                                    disabled={batchDeleting || exporting}
                                >
                                    {TEXT.exportSourceBatch}
                                </Button>
                                <Popconfirm
                                    title={TEXT.batchDeleteConfirmTitle(selectedRowKeys.length)}
                                    description={TEXT.batchDeleteConfirmDesc}
                                    okText={TEXT.deleteOk}
                                    cancelText={TEXT.deleteCancel}
                                    okButtonProps={{ danger: true, loading: batchDeleting }}
                                    onConfirm={handleBatchDelete}
                                >
                                    <Button
                                        size="small"
                                        danger
                                        type="primary"
                                        icon={<DeleteOutlined />}
                                        loading={batchDeleting}
                                    >
                                        {TEXT.batchDeleteAction}
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </Space>
                    )}

                    <div ref={tableContainerRef}>
                        <Table
                            rowKey="id"
                            size="small"
                            columns={columns}
                            dataSource={data}
                            loading={loading}
                            components={tableComponents}
                            scroll={{ x: 2080, y: tableScrollY, scrollToFirstRowOnChange: false }}
                            onRow={(record) => ({
                                onClick: (event) => handleRowClick(record, event),
                            })}
                            rowSelection={{
                                selectedRowKeys,
                                onChange: setSelectedRowKeys,
                                getCheckboxProps: (record) => ({
                                    disabled: !record.id || Boolean(record.isPlaceholderRow),
                                }),
                                preserveSelectedRowKeys: true,
                            }}
                            pagination={false}
                            locale={{
                                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={TEXT.emptyRecords} />,
                            }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                borderTop: '1px solid #f0f0f0',
                                background: '#fafafa',
                                fontSize: 12,
                            }}
                        >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {`${TEXT.totalPrefix} ${total} ${TEXT.totalSuffix}（已加载 ${data.filter((r) => !r?.isPlaceholderRow).length} 条）`}
                            </Text>
                            <Space size={8}>
                                {loadingMore && (
                                    <Space size={6}>
                                        <Spin size="small" />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            正在加载下一批...
                                        </Text>
                                    </Space>
                                )}
                                {!loadingMore && loadMoreError && (
                                    <Space size={6}>
                                        <Text type="danger" style={{ fontSize: 12 }}>
                                            {loadMoreError}
                                        </Text>
                                        <Button size="small" type="link" onClick={loadMore} style={{ padding: 0 }}>
                                            重试加载
                                        </Button>
                                    </Space>
                                )}
                                {!loadingMore && !loadMoreError && hasMore && (
                                    <Button size="small" type="link" onClick={loadMore} style={{ padding: 0 }}>
                                        加载更多
                                    </Button>
                                )}
                                {!loadingMore && !loadMoreError && !hasMore && total > 0 && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        已加载全部记录
                                    </Text>
                                )}
                            </Space>
                        </div>
                    </div>
                </Space>
            </Card>

            <Modal
                title={TEXT.preCheckTitle}
                open={preCheckOpen}
                onCancel={() => {
                    if (uploading) return;
                    setPreCheckOpen(false);
                    setPreCheckBatch([]);
                }}
                footer={(() => {
                    const fresh = preCheckBatch.filter((it) => !it.exists);
                    const all = preCheckBatch;
                    return (
                        <Space>
                            <Button
                                onClick={() => {
                                    setPreCheckOpen(false);
                                    setPreCheckBatch([]);
                                }}
                                disabled={uploading}
                            >
                                {TEXT.preCheckCancel}
                            </Button>
                            <Button
                                onClick={async () => {
                                    setPreCheckOpen(false);
                                    const files = all.map((it) => it.file);
                                    setPreCheckBatch([]);
                                    await doActualUpload(files);
                                }}
                                disabled={uploading || preCheckLoading || all.length === 0}
                            >
                                {TEXT.preCheckUploadAll(all.length)}
                            </Button>
                            <Button
                                type="primary"
                                loading={uploading}
                                disabled={preCheckLoading || fresh.length === 0}
                                onClick={async () => {
                                    setPreCheckOpen(false);
                                    const files = fresh.map((it) => it.file);
                                    setPreCheckBatch([]);
                                    await doActualUpload(files);
                                }}
                            >
                                {TEXT.preCheckUploadFresh(fresh.length)}
                            </Button>
                        </Space>
                    );
                })()}
                width={620}
                destroyOnHidden
            >
                <Spin spinning={preCheckLoading} tip={TEXT.preCheckLoading}>
                    {!preCheckLoading && preCheckBatch.length > 0 && (() => {
                        const fresh = preCheckBatch.filter((it) => !it.exists);
                        const dup = preCheckBatch.filter((it) => it.exists);
                        return (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Space wrap>
                                    <Tag color="blue">
                                        {TEXT.preCheckSummary(preCheckBatch.length, fresh.length, dup.length)}
                                    </Tag>
                                </Space>
                                {fresh.length > 0 && (
                                    <div>
                                        <Text strong>{TEXT.preCheckFreshSection} ({fresh.length})</Text>
                                        <List
                                            size="small"
                                            dataSource={fresh}
                                            style={{ maxHeight: 180, overflow: 'auto' }}
                                            renderItem={(it) => (
                                                <List.Item>
                                                    <Space>
                                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                        <Text>{it.file.name}</Text>
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    </div>
                                )}
                                {dup.length > 0 && (
                                    <div>
                                        <Text strong>{TEXT.preCheckDupSection} ({dup.length})</Text>
                                        <List
                                            size="small"
                                            dataSource={dup}
                                            style={{ maxHeight: 220, overflow: 'auto' }}
                                            renderItem={(it) => {
                                                const r = it.record || {};
                                                const formattedAt = r.uploaded_at
                                                    ? new Date(r.uploaded_at).toLocaleString('sv-SE', { hour12: false }).slice(0, 16)
                                                    : null;
                                                return (
                                                    <List.Item style={{
                                                        background: '#fffbe6',
                                                        borderLeft: '3px solid #faad14',
                                                        paddingLeft: 8,
                                                    }}>
                                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                            <Space wrap>
                                                                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                                                <Text>{it.file.name}</Text>
                                                                {r.status && <Tag color="default">{r.status}</Tag>}
                                                            </Space>
                                                            <Space wrap style={{ paddingLeft: 22 }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {TEXT.duplicateOf}: {r.file_name || '-'}
                                                                    {formattedAt && `  (${TEXT.uploadedAt} ${formattedAt})`}
                                                                </Text>
                                                                {r.id && (
                                                                    <Button
                                                                        type="link"
                                                                        size="small"
                                                                        style={{ padding: 0, height: 'auto' }}
                                                                        onClick={() => {
                                                                            setPreCheckOpen(false);
                                                                            handleOpenDetail({ id: r.id });
                                                                        }}
                                                                    >
                                                                        {TEXT.viewOriginal}
                                                                    </Button>
                                                                )}
                                                            </Space>
                                                        </Space>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </Space>
                        );
                    })()}
                </Spin>
            </Modal>

            <Modal
                title={TEXT.uploadResult}
                open={uploadResultOpen}
                onCancel={() => setUploadResultOpen(false)}
                footer={<Button type="primary" onClick={() => setUploadResultOpen(false)}>{TEXT.close}</Button>}
                width={560}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        <Tag color="blue">{`${TEXT.accepted}: ${uploadResult?.accepted_count ?? 0}`}</Tag>
                        <Tag color="gold">{`${TEXT.duplicate}: ${uploadResult?.duplicate_count ?? 0}`}</Tag>
                        <Tag color="red">{`${TEXT.rejected}: ${uploadResult?.rejected_count ?? 0}`}</Tag>
                    </Space>
                    <List
                        size="small"
                        dataSource={uploadItems}
                        renderItem={renderUploadResultItem}
                        style={{ maxHeight: 360, overflow: 'auto' }}
                    />
                </Space>
            </Modal>

            <Drawer
                title={selectedRecord ? (
                    <Space wrap size={8}>
                        {isImageSource(selectedRecord) ? (
                            <FileImageOutlined style={{ color: '#1677ff' }} />
                        ) : (
                            <FilePdfOutlined style={{ color: '#d4380d' }} />
                        )}
                        <Text strong>{selectedRecord.file_name || selectedRecord.fileName || TEXT.drawerTitle}</Text>
                        {(() => {
                            const meta = STATUS_META[selectedRecord.status] || STATUS_META.pending;
                            return (
                                <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>
                                    {meta.label}
                                </Tag>
                            );
                        })()}
                        {selectedRecord.extraction_confidence != null && (
                            <Tag
                                color={selectedRecord.extraction_confidence >= 0.85 ? 'green' : selectedRecord.extraction_confidence >= 0.7 ? 'gold' : 'orange'}
                                style={{ margin: 0 }}
                            >
                                {`${TEXT.confidence} ${Math.round(selectedRecord.extraction_confidence * 100)}%`}
                            </Tag>
                        )}
                        {selectedRecord.dedup_superseded && (
                            <Tooltip title={TEXT.dedupTagTip}>
                                <Tag color="gold" style={{ margin: 0 }}>{TEXT.dedupTagShort}</Tag>
                            </Tooltip>
                        )}
                        {selectedRecord.page_voided && (
                            <Tooltip title={TEXT.voidedTagTip}>
                                <Tag color="red" style={{ margin: 0 }}>{TEXT.voidedTagShort}</Tag>
                            </Tooltip>
                        )}
                    </Space>
                ) : TEXT.drawerTitle}
                width={1220}
                open={detailOpen}
                onClose={() => {
                    detailRequestRef.current += 1;
                    setDetailLoading(false);
                    setDetailOpen(false);
                    setSelectedRecord(null);
                    setSourcePreview(null);
                    reviewForm.resetFields();
                    clearDetailIdParam();
                }}
                footer={selectedRecord ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Space wrap>
                            <Popconfirm
                                title="确认重新识别该单据？"
                                description="重新识别将保留人工已复核修改的字段；若记录已确认或存在人工字段，模型新识别出的差异将作为待决建议展示，不会直接覆盖当前值。"
                                okText={TEXT.reExtract}
                                cancelText={TEXT.deleteCancel}
                                onConfirm={() => handleReExtract(selectedRecord)}
                            >
                                <Button
                                    icon={<ReloadOutlined />}
                                    disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                >
                                    {TEXT.reExtract}
                                </Button>
                            </Popconfirm>
                        </Space>
                        {selectedRecord.status === 'rejected' ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {selectedRecord.page_voided ? TEXT.voidedReviewHint : TEXT.rejectedReviewHint}
                            </Text>
                        ) : (
                            <Space wrap>
                                <Tooltip title="这个是原单据写错了还是 AI 识别错了？默认按“AI 识别错”帮助系统积累经验；如果是纸面手写笔误或涂改，请勾选此项">
                                    <Checkbox
                                        checked={notModelError}
                                        disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                        onChange={(e) => setNotModelError(e.target.checked)}
                                    >
                                        原单据本身写错（非 AI 看错）
                                    </Checkbox>
                                </Tooltip>
                                <Button
                                    loading={reviewSaving}
                                    disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                    onClick={() => handleReviewSubmit(false)}
                                >
                                    {TEXT.saveReview}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    loading={reviewSaving}
                                    disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                    onClick={() => handleReviewSubmit(true)}
                                >
                                    {TEXT.confirmReview}
                                </Button>
                            </Space>
                        )}
                    </div>
                ) : null}
                destroyOnHidden
            >
                <Spin spinning={detailLoading}>
                    {selectedRecord ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '480px minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
                            {/* 左栏：固定粘性原件预览，右侧滚动复核字段时始终同屏可见 */}
                            <div style={{ position: 'sticky', top: 0 }}>
                                <Card
                                    size="small"
                                    title={TEXT.sourcePreview}
                                    extra={sourcePreview?.signed_url ? (
                                        <a href={sourcePreview.signed_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                                            新窗口打开
                                        </a>
                                    ) : null}
                                >
                                    {sourcePreview?.signed_url ? (
                                        isImageSource(selectedRecord) ? (
                                            <Image
                                                src={sourcePreview.signed_url}
                                                alt={selectedRecord.file_name || selectedRecord.fileName || 'Flight Log'}
                                                style={{ width: '100%', maxHeight: 'calc(100vh - 210px)', objectFit: 'contain', borderRadius: 4 }}
                                            />
                                        ) : (
                                            <iframe
                                                title={selectedRecord.file_name || selectedRecord.fileName || 'Flight Log'}
                                                src={sourcePreview.signed_url}
                                                style={{
                                                    width: '100%',
                                                    height: 'calc(100vh - 210px)',
                                                    minHeight: 520,
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 4,
                                                }}
                                            />
                                        )
                                    ) : (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={TEXT.previewLoadFailed} />
                                    )}
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#8c8c8c' }}>
                                        <Text type="secondary" ellipsis style={{ maxWidth: 340, fontSize: 12 }}>
                                            {selectedRecord.file_name || selectedRecord.fileName || '-'}
                                        </Text>
                                        {(() => {
                                            const srcMeta = SOURCE_TYPE_META[selectedRecord.source_type] || SOURCE_TYPE_META.manual_upload;
                                            return <Tag color={srcMeta.color} style={{ margin: 0 }}>{srcMeta.label}</Tag>;
                                        })()}
                                    </div>
                                </Card>
                            </div>

                            {/* 右栏：状态提醒 + 分区字段复核 + AI 查证过程 + 折叠原始 JSON */}
                            <Space direction="vertical" size={12} style={{ width: '100%', minWidth: 0 }}>
                                {selectedRecord.status === 'review_required' && (
                                    <Alert
                                        showIcon
                                        type="warning"
                                        message={TEXT.reviewReason}
                                        description={getReviewReasonText(selectedRecord) || TEXT.noReviewReason}
                                    />
                                )}
                                {selectedRecord.dedup_superseded && (
                                    <Alert
                                        showIcon
                                        type="warning"
                                        message={TEXT.dedupTag}
                                        description={(
                                            <Space direction="vertical" size={4}>
                                                <Text>{TEXT.dedupDrawerAlert}</Text>
                                                <Space>
                                                    {selectedRecord.superseded_by && (
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            style={{ padding: 0, height: 'auto' }}
                                                            onClick={() => handleOpenDetail({ id: selectedRecord.superseded_by })}
                                                        >
                                                            {TEXT.dedupGotoMain}
                                                        </Button>
                                                    )}
                                                    <Popconfirm
                                                        title={TEXT.dedupRestoreConfirmTitle}
                                                        description={TEXT.dedupRestoreConfirmDesc}
                                                        okText={TEXT.dedupRestore}
                                                        cancelText={TEXT.deleteCancel}
                                                        onConfirm={() => handleDedupRestore(selectedRecord)}
                                                    >
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            style={{ padding: 0, height: 'auto' }}
                                                            loading={dedupRestoringId === selectedRecord.id}
                                                        >
                                                            {TEXT.dedupRestore}
                                                        </Button>
                                                    </Popconfirm>
                                                </Space>
                                            </Space>
                                        )}
                                    />
                                )}
                                {selectedRecord.page_voided && (
                                    <Alert
                                        showIcon
                                        type="error"
                                        message={TEXT.voidedTag}
                                        description={(
                                            <Space direction="vertical" size={4}>
                                                <Text>{TEXT.voidedDrawerAlert}</Text>
                                                <Popconfirm
                                                    title={TEXT.unvoidConfirmTitle}
                                                    description={TEXT.unvoidConfirmDesc}
                                                    okText={TEXT.unvoid}
                                                    cancelText={TEXT.deleteCancel}
                                                    onConfirm={() => handleUnvoid(selectedRecord)}
                                                >
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        style={{ padding: 0, height: 'auto' }}
                                                        loading={unvoidingId === selectedRecord.id}
                                                    >
                                                        {TEXT.unvoid}
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        )}
                                    />
                                )}
                                {selectedRecord.source_type === 'external_crawl' && selectedRecord.external_crawl_file && (
                                    <Alert
                                        showIcon
                                        type="info"
                                        message={TEXT.sourceCrawlInfoTitle}
                                        description={(
                                            <a
                                                href={`/settlement/external-crawl-config?config_id=${selectedRecord.external_crawl_file.url_config_id}&file_id=${selectedRecord.external_crawl_file.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {TEXT.sourceCrawlLinkText(
                                                    selectedRecord.external_crawl_file.id,
                                                    selectedRecord.external_crawl_file.site_key,
                                                    selectedRecord.external_crawl_file.date_key,
                                                )}
                                            </a>
                                        )}
                                    />
                                )}
                                {selectedRecord.source_type === 'external_crawl'
                                    && selectedRecord.external_crawl_file_id
                                    && !selectedRecord.external_crawl_file && (
                                        <Alert
                                            showIcon
                                            type="warning"
                                            message={TEXT.sourceCrawlInfoTitle}
                                            description={TEXT.sourceCrawlDeleted}
                                        />
                                    )}
                                <ReextractSuggestionsBanner
                                    record={selectedRecord}
                                    fieldLabels={FIELD_LABEL_MAP}
                                    disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                    onDecideSuggestions={handleReextractSuggestionsDecision}
                                />

                                <style>{`
                                    .ant-drawer .flight-log-review-form .ant-form-item,
                                    .ant-drawer .flight-log-review-form .ant-form-item-row {
                                        min-width: 0;
                                        max-width: 100%;
                                        display: flex;
                                        flex-direction: column;
                                        align-items: stretch;
                                    }
                                    .ant-drawer .flight-log-review-form .ant-form-item-label {
                                        min-width: 0;
                                        padding-right: 0;
                                    }
                                    .ant-drawer .flight-log-review-form .ant-form-item-control,
                                    .ant-drawer .flight-log-review-form .ant-form-item-control-input,
                                    .ant-drawer .flight-log-review-form .ant-form-item-control-input-content {
                                        min-width: 0 !important;
                                        width: 100%;
                                        max-width: 100%;
                                    }
                                    .ant-drawer .flight-log-review-form .ant-input,
                                    .ant-drawer .flight-log-review-form .ant-input-number,
                                    .ant-drawer .flight-log-review-form .ant-select,
                                    .ant-drawer .flight-log-review-form .ant-picker {
                                        width: 100% !important;
                                        max-width: 100%;
                                        min-width: 0;
                                    }
                                `}</style>
                                <Form form={reviewForm} className="flight-log-review-form" layout="vertical">
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        {REVIEW_FIELD_GROUPS.map((group) => (
                                            <Card key={group.key} size="small" title={group.title}>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: '4px 16px' }}>
                                                    {group.items.map(([name, label, type, span = 4]) => {
                                                        const mark = getFieldMark(selectedRecord, name);
                                                        const unconfirmed = isFieldUnconfirmed(selectedRecord, name);
                                                        const fieldLabel = (mark || unconfirmed) ? (
                                                            <Space size={4} wrap>
                                                                <span>{label}</span>
                                                                {mark && (
                                                                    <Tooltip title={buildMarkTooltip(mark)}>
                                                                        <Tag color="purple" style={{ margin: 0, lineHeight: '16px' }}>
                                                                            {TEXT.derivedTag}
                                                                        </Tag>
                                                                    </Tooltip>
                                                                )}
                                                                {unconfirmed && (
                                                                    <UnconfirmedFieldBadge
                                                                        record={selectedRecord}
                                                                        field={name}
                                                                        notModelError={notModelError}
                                                                        disabled={selectedRecord.status === 'pending' || selectedRecord.status === 'recognizing'}
                                                                        onDecide={handleFieldAdjustmentDecision}
                                                                    />
                                                                )}
                                                            </Space>
                                                        ) : label;
                                                        return (
                                                            <Form.Item
                                                                key={name}
                                                                name={name}
                                                                label={fieldLabel}
                                                                style={{ gridColumn: `span ${span}`, marginBottom: 10, minWidth: 0 }}
                                                            >
                                                                {type === 'number' ? (
                                                                    <InputNumber controls={false} style={{ width: '100%' }} />
                                                                ) : type === 'text' ? (
                                                                    <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                                                                ) : type === 'fleet' ? (
                                                                    <Select
                                                                        showSearch
                                                                        allowClear
                                                                        placeholder="A/C Reg"
                                                                        options={fleetOptions.map((reg) => ({ value: reg, label: reg }))}
                                                                        notFoundContent={fleetOptions.length === 0 ? TEXT.fleetEmptyTip : undefined}
                                                                    />
                                                                ) : (
                                                                    <Input />
                                                                )}
                                                            </Form.Item>
                                                        );
                                                    })}
                                                    {group.includeReviewNotes && (
                                                        <Form.Item
                                                            name="review_notes"
                                                            label={TEXT.reviewNotes}
                                                            style={{ gridColumn: 'span 12', marginBottom: 4, minWidth: 0 }}
                                                        >
                                                            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                    </Space>
                                </Form>

                                {(() => {
                                    const trace = getToolTrace(selectedRecord);
                                    return trace && trace.length > 0 ? (
                                        <ToolTraceViewer trace={trace} />
                                    ) : null;
                                })()}

                                <Collapse
                                    size="small"
                                    items={[
                                        {
                                            key: 'raw-json',
                                            label: TEXT.rawResult,
                                            children: (
                                                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 360, overflow: 'auto', fontSize: 12 }}>
                                                    {JSON.stringify(selectedRecord.recognition_result || selectedRecord.extracted_data || selectedRecord, null, 2)}
                                                </pre>
                                            ),
                                        },
                                    ]}
                                />
                            </Space>
                        </div>
                    ) : null}
                </Spin>
            </Drawer>
        </div>
    );
};

export default FlightLogRecognitionPanel;
